import { useState, useEffect } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { updateProfile } from '@traction/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Check, KeyRound, Plus, Trash2 } from 'lucide-react';

interface EducationEntry {
  school: string;
  degree: string;
  field: string;
  year: number;
}

interface EmploymentEntry {
  company: string;
  title: string;
  startYear: number;
  endYear?: number;
}

export function ProfilePage() {
  const { user, profile, refreshProfile } = useAuth();

  // Profile fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [substackUrl, setSubstackUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [location, setLocation] = useState('');
  const [currentTitle, setCurrentTitle] = useState('');
  const [emailSignature, setEmailSignature] = useState('');

  // Education & Employment
  const [education, setEducation] = useState<EducationEntry[]>([]);
  const [employmentHistory, setEmploymentHistory] = useState<EmploymentEntry[]>([]);

  // UI state
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      // Use first_name/last_name if available, otherwise split full_name
      if (profile.first_name) {
        setFirstName(profile.first_name);
        setLastName(profile.last_name || '');
      } else if (profile.full_name) {
        const parts = profile.full_name.split(' ');
        setFirstName(parts[0] || '');
        setLastName(parts.slice(1).join(' ') || '');
      }
      setCompany(profile.company_name || '');
      setCompanyWebsite(profile.company_website || '');
      setLinkedinUrl(profile.linkedin_url || '');
      setTiktokUrl(profile.tiktok_url || '');
      setFacebookUrl(profile.facebook_url || '');
      setInstagramUrl(profile.instagram_url || '');
      setSubstackUrl(profile.substack_url || '');
      setTwitterUrl(profile.twitter_url || '');
      setYoutubeUrl(profile.youtube_url || '');
      setPhone(profile.phone || '');
      setCity(profile.city || '');
      setLocation(profile.location || '');
      setCurrentTitle(profile.current_title || '');
      setEmailSignature(profile.email_signature || '');
      // Load education and employment history
      setEducation(profile.education || []);
      setEmploymentHistory(profile.employment_history || []);
    }
    if (user) {
      setEmail(user.email || '');
    }
  }, [profile, user]);

  async function handleSave() {
    if (!user) return;

    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      await updateProfile(user.uid, {
        first_name: firstName,
        last_name: lastName,
        full_name: `${firstName} ${lastName}`.trim(),
        company_name: company,
        company_website: companyWebsite || null,
        linkedin_url: linkedinUrl || null,
        tiktok_url: tiktokUrl || null,
        facebook_url: facebookUrl || null,
        instagram_url: instagramUrl || null,
        substack_url: substackUrl || null,
        twitter_url: twitterUrl || null,
        youtube_url: youtubeUrl || null,
        phone: phone || null,
        city: city || null,
        location: location || null,
        current_title: currentTitle || null,
        email_signature: emailSignature || null,
        education: education,
        employment_history: employmentHistory,
      });
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // Education helpers
  function addEducation() {
    setEducation([...education, { school: '', degree: '', field: '', year: new Date().getFullYear() }]);
  }

  function updateEducation(index: number, field: keyof EducationEntry, value: string | number) {
    const updated = [...education];
    updated[index] = { ...updated[index], [field]: value };
    setEducation(updated);
  }

  function removeEducation(index: number) {
    setEducation(education.filter((_, i) => i !== index));
  }

  // Employment helpers
  function addEmployment() {
    setEmploymentHistory([...employmentHistory, { company: '', title: '', startYear: new Date().getFullYear() }]);
  }

  function updateEmployment(index: number, field: keyof EmploymentEntry, value: string | number | undefined) {
    const updated = [...employmentHistory];
    updated[index] = { ...updated[index], [field]: value };
    setEmploymentHistory(updated);
  }

  function removeEmployment(index: number) {
    setEmploymentHistory(employmentHistory.filter((_, i) => i !== index));
  }

  async function handlePasswordReset() {
    if (!email) return;

    setResetError(null);
    setResetSent(false);

    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err: any) {
      console.error('Failed to send reset email:', err);
      setResetError(err.message || 'Failed to send reset email');
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-xl">
      <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">Profile</h1>
      <p className="mt-1 text-sm text-cacao-muted">Your account information.</p>

      <div className="mt-6 space-y-4">
        {/* Name Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-cacao-muted mb-1.5">First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
              placeholder="First name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-cacao-muted mb-1.5">Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
              placeholder="Last name"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-1.5">Email</label>
          <input
            type="email"
            value={email}
            disabled
            className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-sm text-cacao-muted cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-cacao-text-tertiary">Email cannot be changed</p>
        </div>

        {/* Company */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-1.5">Company</label>
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
            placeholder="Your company name"
          />
        </div>

        {/* Company Website */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-1.5">Company Website</label>
          <input
            type="url"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
            className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
            placeholder="https://yourcompany.com"
          />
        </div>

        {/* Social Media Links */}
        <div className="rounded-md border border-cacao-dark-tertiary bg-cacao-dark p-4">
          <p className="text-xs font-medium text-cacao-gold mb-3">Social Media Profiles</p>
          <p className="text-xs text-cacao-muted mb-4">Add your social media profiles (all optional).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">LinkedIn</label>
              <input
                type="url"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">Twitter / X</label>
              <input
                type="url"
                value={twitterUrl}
                onChange={(e) => setTwitterUrl(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="https://x.com/yourhandle"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">Instagram</label>
              <input
                type="url"
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="https://instagram.com/yourhandle"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">Facebook</label>
              <input
                type="url"
                value={facebookUrl}
                onChange={(e) => setFacebookUrl(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="https://facebook.com/yourprofile"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">TikTok</label>
              <input
                type="url"
                value={tiktokUrl}
                onChange={(e) => setTiktokUrl(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="https://tiktok.com/@yourhandle"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">YouTube</label>
              <input
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="https://youtube.com/@yourchannel"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">Substack</label>
              <input
                type="url"
                value={substackUrl}
                onChange={(e) => setSubstackUrl(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="https://yourname.substack.com"
              />
            </div>
          </div>
        </div>

        {/* Phone & City - for email templates */}
        <div className="rounded-md border border-cacao-dark-tertiary bg-cacao-dark p-4">
          <p className="text-xs font-medium text-cacao-gold mb-3">For Email Templates</p>
          <p className="text-xs text-cacao-muted mb-4">These fields are used in your outreach email templates.</p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">Your Title (for emails)</label>
              <input
                type="text"
                value={currentTitle}
                onChange={(e) => setCurrentTitle(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="e.g., CTO, Fractional CTO, Senior Developer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">City (for emails)</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="e.g., Los Angeles, New York, San Francisco"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">Mobile Phone (for emails)</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                placeholder="(310) 555-1234"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-cacao-muted mb-1.5">Email Signature</label>
              <textarea
                value={emailSignature}
                onChange={(e) => setEmailSignature(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none resize-none"
                placeholder="David Bramante&#10;Fractional CTO | Senior Web Developer&#10;david@example.com"
              />
              <p className="mt-1 text-xs text-cacao-text-tertiary">This will be appended to all generated emails.</p>
            </div>
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-1.5">Full Location</label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
            placeholder="City, State, Country"
          />
        </div>

        {/* Education Section */}
        <div className="pt-4 mt-4 border-t border-cacao-dark-tertiary">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-[#F5F5F5]">Education</h2>
            <button
              type="button"
              onClick={addEducation}
              className="inline-flex items-center gap-1 text-xs text-cacao-gold hover:text-cacao-gold-hover transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          </div>
          {education.length === 0 ? (
            <p className="text-xs text-cacao-text-tertiary">No education added yet.</p>
          ) : (
            <div className="space-y-4">
              {education.map((edu, index) => (
                <div key={index} className="rounded-md border border-cacao-dark-tertiary bg-cacao-dark p-4">
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={() => removeEducation(index)}
                      className="text-cacao-text-tertiary hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs text-cacao-muted mb-1">School</label>
                      <input
                        type="text"
                        value={edu.school}
                        onChange={(e) => updateEducation(index, 'school', e.target.value)}
                        className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                        placeholder="University name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-cacao-muted mb-1">Degree</label>
                      <input
                        type="text"
                        value={edu.degree}
                        onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                        className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                        placeholder="e.g., MBA, BS, PhD"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-cacao-muted mb-1">Field of Study</label>
                      <input
                        type="text"
                        value={edu.field}
                        onChange={(e) => updateEducation(index, 'field', e.target.value)}
                        className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                        placeholder="e.g., Computer Science"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-cacao-muted mb-1">Graduation Year</label>
                      <input
                        type="number"
                        value={edu.year}
                        onChange={(e) => updateEducation(index, 'year', parseInt(e.target.value) || 0)}
                        className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                        placeholder="2020"
                        min={1950}
                        max={2030}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Employment History Section */}
        <div className="pt-4 mt-4 border-t border-cacao-dark-tertiary">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-[#F5F5F5]">Employment History</h2>
            <button
              type="button"
              onClick={addEmployment}
              className="inline-flex items-center gap-1 text-xs text-cacao-gold hover:text-cacao-gold-hover transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          </div>
          {employmentHistory.length === 0 ? (
            <p className="text-xs text-cacao-text-tertiary">No employment history added yet.</p>
          ) : (
            <div className="space-y-4">
              {employmentHistory.map((emp, index) => (
                <div key={index} className="rounded-md border border-cacao-dark-tertiary bg-cacao-dark p-4">
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={() => removeEmployment(index)}
                      className="text-cacao-text-tertiary hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-cacao-muted mb-1">Company</label>
                      <input
                        type="text"
                        value={emp.company}
                        onChange={(e) => updateEmployment(index, 'company', e.target.value)}
                        className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-cacao-muted mb-1">Title</label>
                      <input
                        type="text"
                        value={emp.title}
                        onChange={(e) => updateEmployment(index, 'title', e.target.value)}
                        className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                        placeholder="Job title"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-cacao-muted mb-1">Start Year</label>
                      <input
                        type="number"
                        value={emp.startYear}
                        onChange={(e) => updateEmployment(index, 'startYear', parseInt(e.target.value) || 0)}
                        className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                        placeholder="2018"
                        min={1950}
                        max={2030}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-cacao-muted mb-1">End Year (blank if current)</label>
                      <input
                        type="number"
                        value={emp.endYear || ''}
                        onChange={(e) => updateEmployment(index, 'endYear', e.target.value ? parseInt(e.target.value) : undefined)}
                        className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
                        placeholder="Present"
                        min={1950}
                        max={2030}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <p className="text-xs text-red-400">{error}</p>
        )}

        <div className="pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-cacao-gold px-4 py-2.5 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="h-4 w-4" />
                Saved!
              </>
            ) : (
              'Save Profile'
            )}
          </button>
        </div>

        {/* Password Reset Section */}
        <div className="pt-6 mt-6 border-t border-cacao-dark-tertiary">
          <h2 className="text-sm font-medium text-[#F5F5F5]">Password</h2>
          <p className="mt-1 text-xs text-cacao-muted">
            Reset your password via email.
          </p>

          {resetSent ? (
            <p className="mt-3 text-xs text-green-400">
              Password reset email sent to {email}. Check your inbox.
            </p>
          ) : (
            <>
              {resetError && (
                <p className="mt-2 text-xs text-red-400">{resetError}</p>
              )}
              <button
                onClick={handlePasswordReset}
                className="mt-3 inline-flex items-center gap-2 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
              >
                <KeyRound className="h-3.5 w-3.5" />
                Send Password Reset Email
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
